// Operator-impersonation handoff consumer.
//
// admin-service mints a short-lived (<=15m) impersonation access token and hands
// it to the main app by opening `<main_app>/#impersonate=<jwt>` in a new tab. The
// token rides in the URL *fragment* (never the query string, so it is not sent in
// Referer or written to server access logs), but a fragment still lands in
// `window.location.hash`, the tab's address bar, and session history — and any
// telemetry that captured `location.href` would exfiltrate a live bearer token.
//
// This module is the consumer the producer always assumed existed:
//   1. On boot, BEFORE any Sentry/PostHog/RUM init, read `#impersonate=<jwt>`.
//   2. If present, seed the in-memory tokenStore with it (NOT localStorage — same
//      XSS posture as the normal session).
//   3. Immediately `history.replaceState` to strip the fragment from the URL so it
//      never reaches the address bar history or any later telemetry hook.
//   4. Decode the token; if it carries `impersonated_by`, return the banner info so
//      the app can render a persistent "Impersonating <email> (mode) — Exit" banner.
//
// The token is single-use-ish by virtue of its short server-side TTL; the fragment
// scrub + replaceState is the load-bearing client-side fix.

import { decodeJwt } from "@/lib/auth/jwt";
import { tokenStore } from "@/lib/auth/tokenStore";

const FRAGMENT_KEY = "impersonate";

export interface ImpersonationSession {
  /** The operator (actor) user id that initiated the impersonation. */
  impersonatedBy: number;
  /** read_only ("view-only") | act_as. */
  mode: "read_only" | "act_as";
  /** The impersonated user's email (display only). */
  email: string;
}

let _activeSession: ImpersonationSession | null = null;

/** The impersonation session seeded at boot, if any. */
export function getImpersonationSession(): ImpersonationSession | null {
  return _activeSession;
}

/** Pull `impersonate` out of a raw `location.hash` value (e.g. "#impersonate=ey..").
 * Returns the token or null. Tolerates other fragment params being present. */
function parseImpersonateFragment(hash: string): string | null {
  if (!hash) return null;
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  // The fragment may be just `impersonate=<jwt>` or part of `a=b&impersonate=<jwt>`.
  const params = new URLSearchParams(raw);
  const token = params.get(FRAGMENT_KEY);
  return token && token.length > 0 ? token : null;
}

/**
 * Consume the `#impersonate=<jwt>` handoff fragment if present. Idempotent and
 * safe to call once at boot. MUST run before any telemetry init so the token is
 * scrubbed from the URL before anything can capture `location.href`.
 *
 * @returns the decoded impersonation session, or null when no handoff is present.
 */
export function consumeImpersonationHandoff(): ImpersonationSession | null {
  if (typeof window === "undefined") return null;

  const token = parseImpersonateFragment(window.location.hash);
  if (!token) return null;

  // 1. Seed the in-memory session FIRST so a teardown failure can't leave the
  //    token in the URL but not in the store.
  tokenStore.set(token);

  // 2. Strip the fragment from the URL immediately (before telemetry can read it).
  //    Preserve pathname + query; drop only the hash. Guard replaceState so a
  //    sandboxed/about:blank context can't throw and abort boot.
  try {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  } catch {
    // Best effort: even if replaceState is unavailable, the token is already in
    // memory and we proceed (the fragment is at worst still in the address bar).
  }

  // 3. Decode for the banner. The signature is NOT verified here (that is the
  //    API's job on every request); we only read display claims.
  const claims = decodeJwt(token);
  if (!claims || claims.impersonated_by == null) {
    // A non-impersonation token in the fragment: still scrubbed above, but there
    // is no banner to render.
    return null;
  }

  _activeSession = {
    impersonatedBy: Number(claims.impersonated_by),
    mode: claims.impersonation_mode === "act_as" ? "act_as" : "read_only",
    email: claims.email ?? "user",
  };
  return _activeSession;
}

/** Exit the impersonation session: clear the in-memory token and drop the banner.
 * The caller decides where to navigate afterwards. */
export function exitImpersonation(): void {
  _activeSession = null;
  tokenStore.clear();
}
